import api from './api';

/**
 * User Service
 * Handles all user profile-related API calls
 */
const userService = {
  /**
   * Get current user profile
   * @returns {Promise<{user: Object}>}
   */
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data.data; // { user }
  },
  
  /**
   * Update user profile
   * @param {Object} profileData - {firstName, lastName}
   * @returns {Promise<{user: Object}>}
   */
  updateProfile: async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    return response.data.data; // { user }
  },
  
  /**
   * Change password
   * @param {Object} passwordData - {currentPassword, newPassword}
   * @returns {Promise<{message: string}>}
   */
  changePassword: async (passwordData) => {
    const response = await api.put('/users/password', passwordData);
    return response.data; // { success, message }
  },
  
  /**
   * Get users (scope-aware: system_admin → all, company_owner → own company, individual → self)
   * @param {Object} params - {search, userType, status, companyId, individualOnly, page, limit}
   * @returns {Promise<{users: Array, total: number, page: number, limit: number}>}
   */
  getUsers: async (params = {}) => {
    const { search, userType, status, companyId, individualOnly, page, limit } = params;
    const queryParams = new URLSearchParams();
    
    if (search) queryParams.append('search', search);
    if (userType) queryParams.append('userType', userType);
    if (status) queryParams.append('status', status);
    if (companyId) queryParams.append('companyId', companyId);
    if (individualOnly) queryParams.append('individualOnly', 'true');
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    
    const response = await api.get(`/users?${queryParams}`);
    return response.data.data; // { users, total, page, limit }
  },
  
  /**
   * Get user by ID (scope-aware)
   * @param {string} userId
   * @returns {Promise<{user: Object}>}
   */
  getUserById: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data.data; // { user }
  },
  
  /**
   * Update user (scope-aware)
   * @param {string} userId
   * @param {Object} userData
   * @returns {Promise<{user: Object}>}
   */
  updateUser: async (userId, userData) => {
    const response = await api.patch(`/users/${userId}`, userData);
    return response.data.data; // { user }
  },
  
  /**
   * Delete user (scope-aware, soft delete)
   * @param {string} userId
   * @returns {Promise<{message: string}>}
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data; // { success, message }
  },

  /**
   * Create user (scope-aware)
   * @param {Object} userData - { firstName, lastName, email, userType, companyId, password }
   * @returns {Promise<{user: Object}>}
   */
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data.data; // { user }
  }
};

export default userService;

